import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
} from '@apollo/client'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { ApolloProvider } from '@apollo/client/react'
import { getMainDefinition, print } from '@apollo/client/utilities'
import { emitEvent } from '@faceup/devtools'
import { useNotification } from '@faceup/ui-base'
import {
  buildUrl,
  DEFAULT_LANGUAGE,
  errorMessages,
  httpAnalyzerEnabled,
  httpAnalyzerName,
  INVALID_LOGIN_CREDENTIALS_ERROR,
  type Region,
  regionHeaderName,
  storageGet,
} from '@faceup/utils'
import { createClient } from 'graphql-ws'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { map } from 'rxjs'
import { possibleTypes } from '../__generated__/possibleTypes.json'
import { httpAnalyzer } from '../httpAnalyzer'
import { useNetworkStatus } from '../utils/useNetworkStatus'
import { region } from '../utils/useRegion'
import { StorageKeys } from '../utils/useStorage'
import { resolveClientKind } from './ClientProvider'
import { getRequestLanguage } from './requestLanguage'

/**
 * Add an error message here if you don't want to show the default error notification.
 */
const suppressedErrors: string[] = [
  INVALID_LOGIN_CREDENTIALS_ERROR,
  errorMessages.institutionDoesNotExist,
  errorMessages.reportSourceDoesNotExist,
  errorMessages.reportSolvedNoNewChatMessages,
]

// Read from the store that matches the resolved client kind (app/PWA ->
// localStorage, web -> sessionStorage), mirroring useStorage. Reading both
// localStorage-first let a stale token from a prior app/PWA session on the same
// origin shadow the current web session's token.
const getJwt = () =>
  storageGet(resolveClientKind() === 'app' ? 'localStorage' : 'sessionStorage', StorageKeys.JWT)

type CustomApolloContextProps = {
  apiErrorMessage: string
  setApiErrorMessage: (message: string) => void
}

const CustomApolloContext = createContext<CustomApolloContextProps>({
  apiErrorMessage: '',
  setApiErrorMessage: () => undefined,
})

export const useCustomApollo = () => useContext(CustomApolloContext)

// Module-level handle to drop the authenticated reporter's cached data on report
// logout. The client is no longer rebuilt on language change, so it has to be
// cleared explicitly. Kept as a module singleton (like the region handle) because
// report logout can run above this provider in the tree (e.g. AppLockProvider).
//
// We evict only the authenticated viewer subtree, NOT the whole store: report
// logout runs from useAutoVictimLogin's unmount cleanup as we navigate off
// ReportSent, so a full clearStore() would race the destination page's public
// queries (page/report-source) and strand them with no data and no refetch
// (clearStore does not refetch active queries) - the homepage then hangs on its
// skeleton. companyVictimViewer is the only authenticated ROOT_QUERY field; evicting
// it + gc() drops the cached report ciphertext and metadata while leaving the public
// data the next page renders from untouched. (Apollo only ever holds ciphertext +
// metadata; the decrypted bodies live in the @faceup/report decrypt cache.)
let clearApolloStore: () => Promise<unknown> = () => Promise.resolve()
export const clearApolloCache = () => clearApolloStore()

export const createApiHttpLink = (uri: string): ApolloLink =>
  new HttpLink({
    uri,
    credentials: 'include',
    headers: { 'Apollo-Require-Preflight': 'true' },
  })

type InnerCustomApolloProviderProps = {
  children: ReactNode
}

const InnerCustomApolloProvider = ({ children }: InnerCustomApolloProviderProps) => {
  const networkStatus = useNetworkStatus()
  const notification = useNotification()
  const { apiErrorMessage } = useCustomApollo()

  // Refs keep the single, never-rebuilt links reading fresh values.
  const networkStatusRef = useRef(networkStatus)
  networkStatusRef.current = networkStatus
  const apiErrorMessageRef = useRef(apiErrorMessage)
  apiErrorMessageRef.current = apiErrorMessage

  // inspiration
  // > https://www.apollographql.com/docs/react/advanced/subscriptions.html
  const httpLink = useMemo(() => createApiHttpLink(import.meta.env.VITE_API_URL ?? ''), [])

  const authLink = useMemo(
    () =>
      new ApolloLink((operation, forward) => {
        operation.setContext((context: Record<string, unknown>) => {
          const token = getJwt()

          return {
            ...context,
            start: performance.now(),
            headers: {
              authorization: token ? `Bearer ${token}` : '',
              language: getRequestLanguage(),
              [regionHeaderName]: region,
              ...(httpAnalyzer.getIsEnabled() && { [httpAnalyzerEnabled]: 'true' }),
            },
          }
        })

        return forward(operation).pipe(
          map(response => {
            const xHttpAnalyzer = operation.getContext()['response'].headers.get(httpAnalyzerName)
            if (xHttpAnalyzer) {
              httpAnalyzer.handleXRequestFaceUpTracingJSON(xHttpAnalyzer)
            }

            emitEvent('cache', operation.getContext()['cache']?.data?.data)

            const parsedXHttpAnalyzer = (() => {
              try {
                return JSON.parse(xHttpAnalyzer ?? '{}')
              } catch {
                return {}
              }
            })()

            emitEvent('requests', {
              name: operation.operationName,
              query: print(operation.query),
              variables: operation.variables,
              response: response as Record<string, unknown>,
              httpAnalyzer: {
                ...parsedXHttpAnalyzer,
                duration: performance.now() - operation.getContext()['start'],
              },
              isError: 'errors' in response,
              source: 'gql',
            })

            return response
          })
        )
      }),
    []
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: the notification API is stable; capturing it once keeps the single Apollo client from being rebuilt (which would wipe the cache) on every re-render and language change. Fresh values are read via refs.
  const errorLink = useMemo(
    () =>
      new ErrorLink(({ error }) => {
        const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : undefined
        const networkError = graphQLErrors ? undefined : error
        const suppressError = suppressedErrors.includes(graphQLErrors?.[0]?.message ?? '')

        if (suppressError || networkStatusRef.current !== 'online') {
          return
        }

        const errorMessage = networkError?.message ?? graphQLErrors?.[0]?.message ?? ''
        console.error(errorMessage)
        notification.error({
          message: apiErrorMessageRef.current,
          description: errorMessage,
        })
      }),
    []
  )

  const wsLink = useStableWsLink()

  const link = useMemo(
    () =>
      ApolloLink.from([
        errorLink,
        ApolloLink.split(
          // split based on operation type
          ({ query }) => {
            const definition = getMainDefinition(query)

            return (
              definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
            )
          },
          wsLink,
          authLink.concat(httpLink)
        ),
      ]),
    [wsLink, errorLink, authLink, httpLink]
  )

  const apolloClient = useMemo(
    () =>
      new ApolloClient({
        link,
        cache: new InMemoryCache({ possibleTypes }),
      }),
    [link]
  )

  useEffect(() => {
    clearApolloStore = () => {
      apolloClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'companyVictimViewer' })
      apolloClient.cache.gc()
      return Promise.resolve()
    }
  }, [apolloClient])

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>
}

/**
 * A single, stable Apollo link that forwards subscriptions to a graphql-ws link.
 * The underlying ws client is (re)created lazily when the discovered region
 * changes, so the Apollo client itself is never rebuilt.
 */
const useStableWsLink = () => {
  const wsRef = useRef<{ region: Region; link: GraphQLWsLink } | null>(null)

  const getWsLink = () => {
    if (!wsRef.current || wsRef.current.region !== region) {
      wsRef.current = { region, link: new GraphQLWsLink(createWsClient(region)) }
    }
    return wsRef.current.link
  }

  // GraphQLWsLink is terminal, so forward is unused.
  // biome-ignore lint/correctness/useExhaustiveDependencies: the delegate must be stable; it reads the current ws link lazily via getWsLink
  return useMemo(() => new ApolloLink(operation => getWsLink().request(operation)), [])
}

const createWsClient = (region: Region) =>
  createClient({
    url: buildUrl(import.meta.env.VITE_WS_API_URL ?? '', { [regionHeaderName]: region }),
    lazy: true,
    connectionParams: () => ({
      token: getJwt() || '',
      language: getRequestLanguage() || DEFAULT_LANGUAGE,
      origin: window.location.origin,
      [regionHeaderName]: region,
    }),
    retryAttempts: Infinity,
  })

type CustomApolloProviderProps = {
  children?: ReactNode
}

export const CustomApolloProvider = ({ children }: CustomApolloProviderProps) => {
  const [apiErrorMessage, setApiErrorMessage] = useState('An error occurred. Please try again.')

  return (
    <CustomApolloContext.Provider value={{ apiErrorMessage, setApiErrorMessage }}>
      <InnerCustomApolloProvider>{children}</InnerCustomApolloProvider>
    </CustomApolloContext.Provider>
  )
}
